import { useCallback } from 'react';
import { Routes } from 'types';
import styled from '@emotion/styled';
import { setSessionStorage } from 'utils';
import { STATE_SESSION_STORAGE_KEY } from 'context/context';

type TeleportOption = {
  label: string;
  path: string;
  state?: Record<string, unknown>;
};

type TeleportButton = {
  label: string;
  path: string;
  state?: Record<string, unknown>;
};

type TeleportDropdown = {
  label: string;
  options: TeleportOption[];
};

type TeleportItem = TeleportButton | TeleportDropdown;

function isDropdown(item: TeleportItem): item is TeleportDropdown {
  return 'options' in item;
}

export function DevTeleport({ setAllowNavigationToAllRoutes }: { setAllowNavigationToAllRoutes: () => void }) {
  const teleport = useCallback(async (path: string, state?: Record<string, unknown>) => {
    setAllowNavigationToAllRoutes();
    setSessionStorage(STATE_SESSION_STORAGE_KEY, state ?? {});
    window.location.href = path;
  }, [setAllowNavigationToAllRoutes]);

  const onDropdownChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>, options: TeleportOption[]) => {
    const idx = Number(e.target.value);
    if (isNaN(idx)) return;
    const option = options[idx];
    if (option) teleport(option.path, option.state);
  }, [teleport]);

  return (
    <Wrapper>
      {items.map((item) =>
        isDropdown(item) ? (
          <select key={item.label} defaultValue="" onChange={(e) => onDropdownChange(e, item.options)}>
            <option value="" disabled>{item.label}</option>
            {item.options.map((opt, idx) => (
              <option key={opt.label} value={idx}>{opt.label}</option>
            ))}
          </select>
        ) : (
          <button key={item.label} onClick={() => teleport(item.path, item.state)}>{item.label}</button>
        )
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #d6ebff;

  button, select {
    padding: 0.5rem 1rem;
    background-color: #d6ebff;
    border: none;
    cursor: pointer;
    font-weight: 600;
  }
`;

const ASSISTED_APPLY_MOCK_STATE = {
  assistedApplyApplicationId: 'mock-application-id',
  contactDetails: {
    emailAddress: 'test@example.com',
    mobileNumber: '0412345678',
    confirmEmail: 'test@example.com',
  },
};

function assistedApplyOptions(source: string): TeleportOption[] {
  return [
    {
      label: 'PCA',
      path: `${Routes.pendingCustomerAcceptance}?source=${source}&appNum=9906455064004`,
      state: ASSISTED_APPLY_MOCK_STATE,
    },
    {
      label: 'POI',
      path: `${Routes.main}?source=${source}&appNum=9906455064004&assessmentId=mockAssessmentId`,
      state: ASSISTED_APPLY_MOCK_STATE,
    },
    {
      label: 'Referred',
      path: `${Routes.assistedApplyReferred}?source=${source}&appNum=9906455064004`,
    },
    {
      label: 'Claim Error',
      path: `${Routes.assistedApplyClaimError}?source=${source}&appNum=9906455064004`,
    },
  ];
}

const items: TeleportItem[] = [
  {
    label: 'Outcome',
    path: Routes.outcome,
  },
  {
    label: 'SMP AU & show Bank Connect',
    path: '/main?source=smp-au&appNum=1234567890123&assessmentId=mockAssessmentId',
  },
  {
    label: 'SMP AU & hide Bank Connect',
    path: '/main?source=smp-au&appNum=1234567890123',
  },
  {
    label: 'SMP NZ & show Bank Connect',
    path: '/main?source=smp-nz&appNum=1234567890123&assessmentId=mockAssessmentId',
  },
  {
    label: 'SMP NZ & hide Bank Connect',
    path: '/main?source=smp-nz&appNum=1234567890123',
  },
  {
    label: 'CLI AU & show Bank Connect',
    path: '/main?source=cli-au&appNum=1234567890123&assessmentId=mockAssessmentId',
  },
  {
    label: 'CLI AU & hide Bank Connect',
    path: '/main?source=cli-au&appNum=1234567890123',
  },
  {
    label: 'Apply NZ & show Bank Connect & in-store channel',
    path: '/main?source=apply-nz&appNum=1234567890123&channel=instore&assessmentId=mockAssessmentId',
  },
  {
    label: 'Apply NZ & hide Bank Connect & default channel',
    path: '/main?source=apply-nz&appNum=1234567890123',
  },
  {
    label: 'Website AU',
    path: '/main?source=website-au',
  },
  {
    label: 'Website NZ',
    path: '/main?source=website-nz',
  },
  {
    label: 'ACH IDV AU - Additional Card Holder Identity Verification AU',
    path: '/ach-consents?operation=ach-au&appNum=1234567890123&dev=true',
  },
  {
    label: 'ACH IDV NZ - Additional Card Holder Identity Verification NZ',
    path: '/ach-consents?operation=ach-nz&appNum=1234567890123&dev=true',
  },
  {
    label: "ACH POA NZ - Additional Card Holder Proof of Address NZ",
    path: '/ach-doc-upload?source=ach-nz&appNum=1234567890123',
  },
  {
    label: 'SMP AML NZ - Anti-Money Laundering NZ',
    path: '/nz-aml-consents?source=smp-aml-nz&appNum=1234567890123&dev=true',
  },
  {
    label: 'Assisted Apply AU',
    options: assistedApplyOptions('assisted-apply-au'),
  },
  {
    label: 'Assisted Apply NZ',
    options: assistedApplyOptions('assisted-apply-nz'),
  },
];
